import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
  ParametresSoudures,
  StructureSoudures,
  TypeSoudure,
  SouduresState,
  DonneesTonnelle
} from '../types';
import { SouduresCalculService } from '../services/SouduresCalculService';

interface SouduresModuleProps {
  donneesTonnelle: DonneesTonnelle;
  onSouduresChange?: (structure: StructureSoudures | null) => void;
  onError?: (error: string) => void;
}

/**
 * Module dynamique pour la gestion des soudures de toute la tonnelle
 * Recalcul automatique intelligent avec prévention des boucles infinies
 */
export const SouduresModule: React.FC<SouduresModuleProps> = ({
  donneesTonnelle,
  onSouduresChange,
  onError
}) => {
  // Service de calcul stable
  const calculService = useMemo(() => new SouduresCalculService(), []);

  // État du module
  const [state, setState] = useState<SouduresState>({
    parametres: {
      actif: false,
      taille: 0.02,
      couleur: '#ff0000',
      
      // Visibilité par type
      afficherMontantPoutre: true,
      afficherTraverseMontant: true,
      afficherPoutrePoutre: true,
      afficherTraverseTraverse: true,
      afficherDoublePoutre: true,
      afficherAccessoires: true,
      
      // Tailles spécifiques
      tailleMontantPoutre: 1.0,
      tailleTraverseMontant: 0.8,
      taillePoutrePoutre: 1.2,
      tailleTraverseTraverse: 0.6,
      tailleDoublePoutre: 0.9,
      tailleAccessoires: 0.7
    },
    structure: null,
    isCalculating: false,
    error: null
  });

  // Plus de cache - recalcul systématique pour réactivité maximale

  // Fonction de calcul mémorisée - RECALCUL SYSTÉMATIQUE
  const calculerSoudures = useCallback(async () => {
    if (!donneesTonnelle || !state.parametres.actif) {
      setState(prev => ({ ...prev, structure: null, isCalculating: false, error: null }));
      onSouduresChange?.(null);
      return;
    }

    setState(prev => ({ ...prev, isCalculating: true, error: null }));

    try {
      // Calcul instantané pour réactivité maximale
      const nouvelleStructure = calculService.calculerToutesSoudures(
        donneesTonnelle,
        state.parametres
      );

      setState(prev => ({
        ...prev,
        structure: nouvelleStructure,
        isCalculating: false
      }));

      onSouduresChange?.(nouvelleStructure);
    } catch (error) {
      const errorMsg = `Erreur calcul soudures: ${error instanceof Error ? error.message : 'Erreur inconnue'}`;
      setState(prev => ({
        ...prev,
        error: errorMsg,
        isCalculating: false
      }));
      onError?.(errorMsg);
    }
  }, [donneesTonnelle, state.parametres, calculService, onSouduresChange, onError]);

  // Effect pour le recalcul automatique avec debounce minimal
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      calculerSoudures();
    }, 10); // Debounce minimal de 10ms pour réactivité maximale

    return () => clearTimeout(timeoutId);
  }, [calculerSoudures]);

  /**
   * Met à jour un paramètre
   */
  const updateParametre = useCallback(<K extends keyof ParametresSoudures>(
    key: K,
    value: ParametresSoudures[K]
  ) => {
    setState(prev => ({
      ...prev,
      parametres: { ...prev.parametres, [key]: value }
    }));
  }, []);

  /**
   * Met à jour la visibilité d'un type de soudure
   */
  const toggleTypeSoudure = useCallback((type: TypeSoudure, visible: boolean) => {
    const paramKey = {
      [TypeSoudure.MONTANT_POUTRE]: 'afficherMontantPoutre',
      [TypeSoudure.TRAVERSE_MONTANT]: 'afficherTraverseMontant',
      [TypeSoudure.POUTRE_POUTRE]: 'afficherPoutrePoutre',
      [TypeSoudure.TRAVERSE_TRAVERSE]: 'afficherTraverseTraverse',
      [TypeSoudure.DOUBLE_POUTRE]: 'afficherDoublePoutre',
      [TypeSoudure.ACCESSOIRE]: 'afficherAccessoires'
    }[type] as keyof ParametresSoudures;

    updateParametre(paramKey, visible);
  }, [updateParametre]);

  const moduleStyles = {
    background: '#2d2d2d',
    border: '1px solid #555',
    borderRadius: '8px',
    padding: '12px',
    marginBottom: '16px',
    color: '#e0e0e0'
  };

  const headerStyles = {
    h3: {
      margin: '0 0 5px 0',
      color: '#4dabf7',
      fontSize: '16px',
      borderBottom: '2px solid #4dabf7',
      paddingBottom: '3px'
    },
    p: {
      margin: '0 0 10px 0',
      color: '#aaa',
      fontSize: '12px'
    }
  };

  const sectionStyles = {
    marginBottom: '12px',
    padding: '10px',
    background: '#3a3a3a',
    borderRadius: '6px',
    border: '1px solid #555'
  };

  const sectionTitleStyles = {
    margin: '0 0 12px 0',
    color: '#4dabf7',
    fontSize: '14px',
    fontWeight: 'bold' as const
  };

  const controleGroupeStyles = {
    marginBottom: '8px',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '4px'
  };

  const controleLabelStyles = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '13px',
    color: '#e0e0e0'
  };

  const controleSliderStyles = {
    width: '100%'
  };

  const controleCouleurStyles = {
    width: '40px',
    height: '30px',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer'
  };

  const statsGridStyles = {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '8px'
  };

  const statItemStyles = {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '6px 8px',
    background: '#2d2d2d',
    borderRadius: '4px',
    fontSize: '12px'
  };

  const statLabelStyles = {
    color: '#aaa'
  };

  const statValueStyles = {
    color: '#4dabf7',
    fontWeight: 'bold'
  };

  const messageStyles = {
    info: { color: '#4dabf7', fontSize: '12px' },
    erreur: { color: '#ff6b6b', fontSize: '12px' },
    succes: { color: '#51cf66', fontSize: '12px' },
    neutre: { color: '#aaa', fontSize: '12px' }
  };

  const typeControlStyles = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '8px',
    padding: '8px',
    background: '#2d2d2d',
    borderRadius: '4px'
  };

  const typeSliderStyles = {
    flex: 1,
    marginLeft: '8px'
  };

  return (
    <div style={moduleStyles}>
      <div>
        <h3 style={headerStyles.h3}>🔗 Module Soudures</h3>
        <p style={headerStyles.p}>Gestion dynamique des soudures de la tonnelle</p>
      </div>

      {/* Activation du module */}
      <div style={controleGroupeStyles}>
        <label style={controleLabelStyles}>
          <input
            type="checkbox"
            checked={state.parametres.actif}
            onChange={(e) => updateParametre('actif', e.target.checked)}
          />
          Activer l'affichage des soudures
          {state.isCalculating && <span style={{ color: '#4dabf7', marginLeft: '8px' }}>⚙️ Calcul...</span>}
        </label>
      </div>

      {state.parametres.actif && (
        <>
          {/* Paramètres globaux */}
          <div style={sectionStyles}>
            <h4 style={sectionTitleStyles}>Paramètres globaux</h4>
            
            <div style={controleGroupeStyles}>
              <label style={controleLabelStyles}>
                Taille globale : {state.parametres.taille.toFixed(3)}m
              </label>
              <input
                type="range"
                min="0.01"
                max="0.15"
                step={0.005}
                value={state.parametres.taille}
                onChange={(e) => updateParametre('taille', parseFloat(e.target.value))}
                style={controleSliderStyles}
              />
            </div>

            <div style={controleGroupeStyles}>
              <label style={controleLabelStyles}>Couleur :</label>
              <input
                type="color"
                value={state.parametres.couleur}
                onChange={(e) => updateParametre('couleur', e.target.value)}
                style={controleCouleurStyles}
              />
            </div>
          </div>

          {/* Types de soudures */}
          <div style={sectionStyles}>
            <h4 style={sectionTitleStyles}>Types de soudures à afficher</h4>
            
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
              <label style={controleLabelStyles}>
                <input
                  type="checkbox"
                  checked={state.parametres.afficherMontantPoutre}
                  onChange={(e) => toggleTypeSoudure(TypeSoudure.MONTANT_POUTRE, e.target.checked)}
                />
                Montant-Poutre
              </label>
              
              <label style={controleLabelStyles}>
                <input
                  type="checkbox"
                  checked={state.parametres.afficherTraverseMontant}
                  onChange={(e) => toggleTypeSoudure(TypeSoudure.TRAVERSE_MONTANT, e.target.checked)}
                />
                Traverse-Montant
              </label>
              
              <label style={controleLabelStyles}>
                <input
                  type="checkbox"
                  checked={state.parametres.afficherPoutrePoutre}
                  onChange={(e) => toggleTypeSoudure(TypeSoudure.POUTRE_POUTRE, e.target.checked)}
                />
                Poutre-Poutre
              </label>
              
              <label style={controleLabelStyles}>
                <input
                  type="checkbox"
                  checked={state.parametres.afficherTraverseTraverse}
                  onChange={(e) => toggleTypeSoudure(TypeSoudure.TRAVERSE_TRAVERSE, e.target.checked)}
                />
                Traverse-Traverse
              </label>
              
              <label style={controleLabelStyles}>
                <input
                  type="checkbox"
                  checked={state.parametres.afficherDoublePoutre}
                  onChange={(e) => toggleTypeSoudure(TypeSoudure.DOUBLE_POUTRE, e.target.checked)}
                />
                Double Poutre
              </label>
              
              <label style={controleLabelStyles}>
                <input
                  type="checkbox"
                  checked={state.parametres.afficherAccessoires}
                  onChange={(e) => toggleTypeSoudure(TypeSoudure.ACCESSOIRE, e.target.checked)}
                />
                Accessoires
              </label>
            </div>
          </div>

          {/* Statistiques */}
          {state.structure && (
            <div style={sectionStyles}>
              <h4 style={sectionTitleStyles}>Statistiques</h4>
              <div style={statsGridStyles}>
                <div style={statItemStyles}>
                  <span style={statLabelStyles}>Total :</span>
                  <span style={statValueStyles}>{state.structure.statistiques.total}</span>
                </div>
                <div style={statItemStyles}>
                   <span style={statLabelStyles}>Montant-Poutre :</span>
                   <span style={statValueStyles}>{state.structure.statistiques.parType[TypeSoudure.MONTANT_POUTRE] || 0}</span>
                 </div>
                 <div style={statItemStyles}>
                   <span style={statLabelStyles}>Traverse-Montant :</span>
                   <span style={statValueStyles}>{state.structure.statistiques.parType[TypeSoudure.TRAVERSE_MONTANT] || 0}</span>
                 </div>
                 <div style={statItemStyles}>
                   <span style={statLabelStyles}>Poutre-Poutre :</span>
                   <span style={statValueStyles}>{state.structure.statistiques.parType[TypeSoudure.POUTRE_POUTRE] || 0}</span>
                 </div>
                 <div style={statItemStyles}>
                   <span style={statLabelStyles}>Traverse-Traverse :</span>
                   <span style={statValueStyles}>{state.structure.statistiques.parType[TypeSoudure.TRAVERSE_TRAVERSE] || 0}</span>
                 </div>
                 <div style={statItemStyles}>
                   <span style={statLabelStyles}>Double Poutre :</span>
                   <span style={statValueStyles}>{state.structure.statistiques.parType[TypeSoudure.DOUBLE_POUTRE] || 0}</span>
                 </div>
                 <div style={statItemStyles}>
                   <span style={statLabelStyles}>Accessoires :</span>
                   <span style={statValueStyles}>{state.structure.statistiques.parType[TypeSoudure.ACCESSOIRE] || 0}</span>
                 </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Messages d'état */}
      <div style={{ minHeight: '40px', display: 'flex', alignItems: 'center' }}>
        {state.error ? (
          <div style={messageStyles.erreur}>Erreur : {state.error}</div>
        ) : state.structure ? (
          <div style={messageStyles.succes}>✓ Soudures calculées automatiquement</div>
        ) : state.parametres.actif ? (
          <div style={messageStyles.info}>En attente de données...</div>
        ) : (
          <div style={messageStyles.neutre}>Module désactivé</div>
        )}
      </div>
    </div>
  );
};

export default SouduresModule;